import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { type IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import { type IResponseFullCampaign } from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  EnrollmentFilters,
  EnrollmentStore,
  initEnrollmentFilters,
} from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'
import { EnrollByWebhookStore } from 'widgets/EnrollByWebhook/store/EnrollByWebhookStore.ts'
import { type CampaignSettingsStore } from 'pages/campaigns/ui/CampaignSettings/store/CampaignSettingsStore'

export class CampaignEnrollContactsStore {
  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _enrollmentExcludeFilters = EnrollmentFilters.Empty()
  private _enrollmentStore: EnrollmentStore
  private _loading = false
  private _number_id: number | null = null
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null
  readonly enrollByWebhookStore: EnrollByWebhookStore

  constructor(
    private _entities: IEntitiesStore,
    private _campaignSettingsStore: CampaignSettingsStore,
    response?: IResponseFullCampaign
  ) {
    makeAutoObservable(this)

    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'campaign',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
        excludeSearchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )
    this.enrollByWebhookStore = new EnrollByWebhookStore(
      response
        ? {
            isActive: response.settings.enroll_by_webhook,
            webhook_url: response.webhook_url,
          }
        : undefined
    )

    this.initFilters()
    this.reactionIsShowContactsAlert()

    this._campaignSettingsStore.setOnReEnterChange((value) => {
      this._enrollmentStore.setIsContactReEnroll(value)
      this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    })
  }

  get enrollmentFilters() {
    return this._enrollmentFilters
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  error = ''
  setError = (value: typeof this.error) => {
    this.error = value
  }

  get loading() {
    return this._loading
  }

  get isEmpty() {
    return !this._enrollmentFilters.allSize && !this._enrollmentExcludeFilters.allSize
  }

  handleReEnroll = (value: boolean) => {
    this._enrollmentStore.setIsContactReEnroll(value)
    this._campaignSettingsStore.setIsReEnter(value)
  }

  get paramsFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this._enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this._enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this._enrollmentFilters.tags?.map((tag) => tag.id),
      advanced: this._enrollmentFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  get paramsExcludeFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this._enrollmentExcludeFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this._enrollmentExcludeFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this._enrollmentExcludeFilters.tags?.map((tag) => tag.id),
      advanced: this._enrollmentExcludeFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  initFilters = () => {
    this._enrollmentStore.initFilters()
  }

  init = async (
    filtersListGroups: IResponseFullCampaign['filters'],
    excludeFiltersListGroups: IResponseFullCampaign['exclude_filters']
  ) => {
    runInAction(() => {
      this._loading = true
    })

    const [filtersData, excludeFiltersData] = await Promise.all([
      initEnrollmentFilters(this._entities, filtersListGroups),
      initEnrollmentFilters(this._entities, excludeFiltersListGroups),
    ])

    if (filtersData) {
      this._enrollmentFilters.sync({
        ...filtersData,
        number_id: this._number_id,
      })
    }

    if (excludeFiltersData) {
      this._enrollmentExcludeFilters.sync({
        ...excludeFiltersData,
        number_id: this._number_id,
      })
    }

    this._enrollmentStore.setIsContactReEnroll(this._campaignSettingsStore.isReEnter)
    this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)

    runInAction(() => {
      this._loading = false
    })
  }

  editEnrollment = async () => {
    const filters = this._enrollmentFilters.clone()
    const excludeFilters = this._enrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters: IEnrollmentActions['confirm'] = async ({ filters, excludeFilters }) => {
      runInAction(() => {
        this._enrollmentFilters = filters
        this._enrollmentExcludeFilters = excludeFilters
      })
      return true
    }

    this._enrollmentStore.openModal({
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
      allowEmptyCount: true,
      title: 'Contacts',
    })
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert = reaction(
      () => [this._enrollmentStore.isContactReEnroll, this._enrollmentFilters.filters],
      () => this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    )
  }
}
